import { useEffect, useRef, useState, useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { generationAPI } from '../services/api';
import { parseArticleContentToHtml } from '../utils/articleParser';
import { ASPECT_RATIOS } from '../constants/aspectRatios';
import './ArticleEditorModal.css';

const AUTOSAVE_DELAY_MS = 1000;
const BASE = (import.meta.env.BASE_URL || '').replace(/\/$/, '');

export default function ArticleEditorModal({ articleId, onClose, aspectRatio: initialAspectRatio = '16:9' }) {
  const [articleData, setArticleData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState('');
  const [imageUrls, setImageUrls] = useState({});
  const [imagePrompts, setImagePrompts] = useState({});
  const [selectedRatio, setSelectedRatio] = useState(initialAspectRatio || '16:9');
  const debounceTimer = useRef(null);
  const isSaving = useRef(false);

  // Fetch article on mount
  useEffect(() => {
    if (!articleId) return;
    setLoading(true);
    generationAPI.getArticle(articleId)
      .then(res => {
        setArticleData(res.data);
        const prompts = {};
        (res.data.images ?? []).forEach(img => { prompts[img.id] = img.prompt ?? ''; });
        setImagePrompts(prompts);
      })
      .catch(err => {
        console.error('Failed to load article:', err);
      })
      .finally(() => setLoading(false));
  }, [articleId]);

  // Load blob URLs for images whenever articleData.images changes
  useEffect(() => {
    const images = articleData?.images ?? [];
    if (images.length === 0) return;

    let cancelled = false;
    const loadBlobs = async () => {
      const entries = await Promise.all(
        images.map(async (img) => {
          try {
            const res = await generationAPI.downloadImage(img.id);
            const blob = new Blob([res.data], { type: res.headers['content-type'] ?? 'image/jpeg' });
            return [img.id, URL.createObjectURL(blob)];
          } catch {
            return [img.id, null];
          }
        })
      );
      if (!cancelled) {
        setImageUrls(Object.fromEntries(entries.filter(([, url]) => url !== null)));
      }
    };
    loadBlobs();
    return () => {
      cancelled = true;
      Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
    };
  }, [articleData?.images]); // eslint-disable-line react-hooks/exhaustive-deps

  // Close on ESC
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  const refreshArticle = useCallback(async () => {
    try {
      const res = await generationAPI.getArticle(articleId);
      setArticleData(res.data);
      const prompts = {};
      (res.data.images ?? []).forEach(img => { prompts[img.id] = img.prompt ?? ''; });
      setImagePrompts(prompts);
      setImageUrls({}); // clear cached blobs; blob effect will reload
    } catch (err) {
      console.error('Failed to refresh article:', err);
    }
  }, [articleId]);

  const getInitialContent = useCallback(() => {
    if (!articleData?.article) return '<p></p>';
    const { edited_content, content } = articleData.article;
    if (edited_content) return edited_content;
    return parseArticleContentToHtml(content);
  }, [articleData]);

  const handleSave = useCallback(async (html) => {
    if (isSaving.current) return;
    isSaving.current = true;
    setSaveStatus('Saving…');
    try {
      await generationAPI.updateArticle(articleId, { edited_content: html });
      setSaveStatus('Saved ✓');
    } catch (err) {
      console.error('Auto-save failed:', err);
      setSaveStatus('Save failed');
    } finally {
      isSaving.current = false;
    }
  }, [articleId]);

  const [busyImages, setBusyImages] = useState({});

  const handleRegenerateImage = useCallback(async (imageId) => {
    const prompt = imagePrompts[imageId] ?? '';
    setBusyImages(prev => ({ ...prev, [imageId]: 'Regenerating…' }));
    try {
      await generationAPI.regenerateImage(imageId, { prompt, aspect_ratio: selectedRatio });
      await refreshArticle();
    } catch (err) {
      console.error('Regenerate failed:', err);
    } finally {
      setBusyImages(prev => { const n = { ...prev }; delete n[imageId]; return n; });
    }
  }, [imagePrompts, selectedRatio, refreshArticle]);

  const handleUpscaleImage = useCallback(async (imageId) => {
    setBusyImages(prev => ({ ...prev, [imageId]: 'Upscaling…' }));
    try {
      await generationAPI.upscaleImages(articleId, { image_ids: [imageId] });
      await refreshArticle();
    } catch (err) {
      console.error('Upscale failed:', err);
    } finally {
      setBusyImages(prev => { const n = { ...prev }; delete n[imageId]; return n; });
    }
  }, [articleId, refreshArticle]);

  const [downloading, setDownloading] = useState(false);
  const handleDownloadZip = useCallback(async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      const res = await generationAPI.downloadZip(articleId);
      const blob = new Blob([res.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${articleData?.article?.title ?? 'article'}.zip`;
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      a.remove();
    } catch (err) {
      console.error('Download ZIP failed:', err);
    } finally {
      setDownloading(false);
    }
  }, [articleId, articleData, downloading]);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: { levels: [2, 3] },
          codeBlock: false,
          code: false,
        }),
      ],
      content: getInitialContent(),
      onUpdate: ({ editor }) => {
        clearTimeout(debounceTimer.current);
        setSaveStatus('');
        debounceTimer.current = setTimeout(() => {
          handleSave(editor.getHTML());
        }, AUTOSAVE_DELAY_MS);
      },
      editable: true,
    },
    // Re-initialise editor when article loads (dependency on articleData)
    [articleData]
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => clearTimeout(debounceTimer.current);
  }, []);

  if (!articleId) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content article-editor-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title">
            {articleData?.article?.title ?? 'Edit Article'}
          </h3>
          {saveStatus && (
            <span className={`save-indicator ${saveStatus === 'Saved ✓' ? 'save-indicator--saved' : saveStatus === 'Saving…' ? 'save-indicator--saving' : 'save-indicator--error'}`}>
              {saveStatus}
            </span>
          )}
          <button
            className="btn-secondary btn-small download-zip-btn"
            onClick={handleDownloadZip}
            disabled={downloading}
          >
            {downloading ? 'Downloading…' : 'Download ZIP'}
          </button>
          <button className="modal-close btn-icon" onClick={onClose} aria-label="Close editor">
            ×
          </button>
        </div>

        <div className="modal-body article-editor-body">
          {loading && <div className="editor-loading">Loading article…</div>}

          {!loading && editor && (
            <div className="tiptap-wrapper">
              <div className="tiptap-toolbar">
                <button
                  className={`toolbar-btn ${editor.isActive('bold') ? 'is-active' : ''}`}
                  onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
                  title="Bold"
                >
                  <strong>B</strong>
                </button>
                <button
                  className={`toolbar-btn ${editor.isActive('italic') ? 'is-active' : ''}`}
                  onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
                  title="Italic"
                >
                  <em>I</em>
                </button>
                <button
                  className={`toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'is-active' : ''}`}
                  onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 2 }).run(); }}
                  title="Heading 2"
                >
                  H2
                </button>
                <button
                  className={`toolbar-btn ${editor.isActive('heading', { level: 3 }) ? 'is-active' : ''}`}
                  onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleHeading({ level: 3 }).run(); }}
                  title="Heading 3"
                >
                  H3
                </button>
                <button
                  className={`toolbar-btn ${editor.isActive('bulletList') ? 'is-active' : ''}`}
                  onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
                  title="Bullet list"
                >
                  ≡
                </button>
              </div>
              <EditorContent editor={editor} className="tiptap-editor" />
            </div>
          )}

          {!loading && (articleData?.images?.length ?? 0) > 0 && (
            <section className="editor-images-section">
              <div className="editor-images-header">
                <h4 className="editor-images-heading">Images</h4>
                <div className="editor-ratio-selector">
                  <span className="editor-ratio-label">Aspect Ratio</span>
                  <div className="editor-ratio-options">
                    {ASPECT_RATIOS.map(r => (
                      <button
                        key={r.value}
                        className={`editor-ratio-btn${selectedRatio === r.value ? ' active' : ''}`}
                        onClick={() => setSelectedRatio(r.value)}
                        title={r.value}
                      >
                        <span className="editor-ratio-thumb" style={{ aspectRatio: `${r.w} / ${r.h}` }} />
                        <span className="editor-ratio-value">{r.value}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="editor-images-grid">
                {articleData.images.map((img) => (
                  <div key={img.id} className="editor-image-card">
                    <div className="editor-image-preview">
                      {imageUrls[img.id] ? (
                        <img src={imageUrls[img.id]} alt={`Image ${img.image_number}`} />
                      ) : (
                        <div className="editor-image-placeholder">Loading…</div>
                      )}
                    </div>
                    <div className="editor-image-controls">
                      <textarea
                        className="editor-image-prompt"
                        rows={2}
                        value={imagePrompts[img.id] ?? img.prompt ?? ''}
                        onChange={e =>
                          setImagePrompts(prev => ({ ...prev, [img.id]: e.target.value }))
                        }
                        placeholder="Change prompt…"
                      />
                      <div className="editor-image-actions">
                        {busyImages[img.id] ? (
                          <span className="editor-image-busy">{busyImages[img.id]}</span>
                        ) : (
                          <>
                            <button
                              className="btn-secondary btn-small"
                              onClick={() => handleRegenerateImage(img.id)}
                            >
                              Regenerate
                            </button>
                            <button
                              className="btn-secondary btn-small"
                              onClick={() => handleUpscaleImage(img.id)}
                            >
                              Upscale
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
